import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FavouriteCurrencyMode, FavouriteCurrencyType, Filter } from '../../../models/filter-models';
import { FilterService } from '../../../services/filter.service';
import { AutocompleteOption } from '../../../common/autocomplete/autocomplete.component';
import { BaseType } from '../../../models/item-data-models';
import { ItemDataService } from '../../../services/item-data.service';
import { FilterTitleComponent } from '../../../common/filter-title/filter-title.component';
import { poe1 } from '../../../../game';
import { CurrencyTierService } from '../../../services/tiering/currency-tier.service';
import { BaseComponent } from '../../../common/base.component';
import { ActiveRules, ActiveSpecialRules } from '../../../services/filter-generator.service';
import { FavouriteBasicCurrencyFilterComponent } from '../favourite-basic-currency-filter/favourite-basic-currency-filter.component';
import { FavouriteCurrencyTypeFilterComponent } from '../favourite-currency-type-filter/favourite-currency-type-filter.component';
import { EndgameContent, EndgameContentIconMap } from '../../../models/enums';

@Component({
  selector: 'app-favourite-currency-filters',
  imports: [FormsModule, FilterTitleComponent, FavouriteBasicCurrencyFilterComponent, FavouriteCurrencyTypeFilterComponent],
  templateUrl: './favourite-currency-filters.component.html',
  styleUrl: './favourite-currency-filters.component.scss'
})
export class FavouriteCurrencyFiltersComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) filter!: Filter;
  @Input({ required: true }) activeRules!: ActiveRules;
  @Input({ required: true }) activeSpecialRules!: ActiveSpecialRules;

  FavouriteCurrencyMode = FavouriteCurrencyMode;

  basicCurrencyOptions: BaseType[];
  scarabContentOptions: AutocompleteOption[];
  currencyItemTypeOptions: AutocompleteOption[];
  rowItemTypeOptions: AutocompleteOption[][] = [];

  constructor(
    private filterService: FilterService,
    private itemDataService: ItemDataService,
    private currencyTierService: CurrencyTierService,
  ) {
    super();

    this.basicCurrencyOptions = this.itemDataService.getFlatItemData()
      .filter(i => i.itemType === 'Currency')
      .map(i => ({ name: i.baseType.name, image: i.baseType.image }));

    this.scarabContentOptions = this.itemDataService.getScarabContentTypes()
      .map(c => ({ name: c, image: EndgameContentIconMap[c] }));

    const itemData = this.itemDataService.getItemData();
    this.currencyItemTypeOptions = this.itemDataService.getFavouriteCurrencyItemTypes()
      .map(t => ({
        name: t,
        image: itemData.find(d => d.itemType === t)?.baseTypes[0]?.image,
      }));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filter']) {
      this.refreshRowOptions();
    }
  }

  refreshRowOptions() {
    this.rowItemTypeOptions = this.filter.favouriteCurrencyTypes.map((entry, index) => {
      const taken = new Set(
        this.filter.favouriteCurrencyTypes.flatMap((e, i) => i === index ? [] : e.itemTypes)
      );
      const available = this.currencyItemTypeOptions.filter(o => !taken.has(o.name));
      const selected = entry.itemTypes
        .filter(t => !available.some(o => o.name === t))
        .map(t => this.currencyItemTypeOptions.find(o => o.name === t) ?? { name: t });
      return [...selected, ...available];
    });
  }

  onBasicModeChanged(mode: FavouriteCurrencyMode) {
    this.filter.favouriteBasicCurrencyMode = mode;
    if (mode === FavouriteCurrencyMode.PromoteTier) {
      this.filter.favouriteBasicCurrencyValue = 1;
    } else if (mode === FavouriteCurrencyMode.Multiplier) {
      this.filter.favouriteBasicCurrencyValue = poe1 ? 1.5 : 2;
    } else {
      this.filter.favouriteBasicCurrencyValue = null;
    }
    this.rebuildCurrencyTiers();
  }

  onScarabsModeChanged(mode: FavouriteCurrencyMode) {
    this.filter.favouriteScarabsMode = mode;
    if (mode === FavouriteCurrencyMode.PromoteTier) {
      this.filter.favouriteScarabsValue = 1;
    } else if (mode === FavouriteCurrencyMode.Multiplier) {
      this.filter.favouriteScarabsValue = poe1 ? 1.5 : 2;
    } else {
      this.filter.favouriteScarabsValue = null;
    }
    this.rebuildCurrencyTiers();
  }

  toggleFavouriteBasicCurrency() {
    this.filter.favouriteBasicCurrency = !this.filter.favouriteBasicCurrency;
    this.rebuildCurrencyTiers();
  }

  toggleFavouriteScarabs() {
    this.filter.favouriteScarabs = !this.filter.favouriteScarabs;
    this.rebuildCurrencyTiers();
  }

  onScarabsTypesChange(types: string[]) {
    const next = types as (EndgameContent | 'None')[];
    this.filter.favouriteScarabsTypesFromPresets = this.filter.favouriteScarabsTypesFromPresets
      .filter(c => next.includes(c));
    this.filter.favouriteScarabsTypes = next.length === 0 ? ['None'] : next;
    this.rebuildCurrencyTiers();
  }

  addFavouriteCurrencyType() {
    const taken = new Set(this.filter.favouriteCurrencyTypes.flatMap(e => e.itemTypes));
    const firstAvailable = this.currencyItemTypeOptions.find(o => !taken.has(o.name))?.name;
    const entry = new FavouriteCurrencyType();
    if (firstAvailable) {
      entry.itemTypes = [firstAvailable];
    }
    this.filter.favouriteCurrencyTypes.push(entry);
    this.refreshRowOptions();
    this.rebuildCurrencyTiers();
  }

  removeFavouriteCurrencyType(index: number) {
    this.filter.favouriteCurrencyTypes.splice(index, 1);
    this.refreshRowOptions();
    this.rebuildCurrencyTiers();
  }

  onRowChanged() {
    this.refreshRowOptions();
    this.rebuildCurrencyTiers();
  }

  updateFilter() {
    this.filterService.updateFilter();
  }

  rebuildCurrencyTiers() {
    this.currencyTierService.combineCurrencyTierListData(this.filter);
    this.updateFilter();
  }
}
